// src/components/homepage_content/QuickSelection.js - Fixed basketball handling

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FootballAPI } from '../../services/footballApi';
import { BasketballAPI } from '../../services/basketballApi';
import { useBetSlip } from '../../context/BetSlipContext';
import MatchCard from '../sports/MatchCard';
import { FiChevronRight, FiEye } from 'react-icons/fi';

const QuickSelection = () => {
  const navigate = useNavigate();
  const { addToBetSlip } = useBetSlip();
  
  const [selectedSport, setSelectedSport] = useState('football');
  const [selectedBetType, setSelectedBetType] = useState('match_winner');
  const [selectedBets, setSelectedBets] = useState([]);
  const [totalOdds, setTotalOdds] = useState(1.0);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [oddsFormat, setOddsFormat] = useState('american');
  const [viewMode, setViewMode] = useState('grid');
  
  // Sports options
  const sports = useMemo(() => [
    { id: 'football', name: 'Football', icon: '⚽', color: 'from-blue-500 to-cyan-400' },
    { id: 'basketball', name: 'Basketball', icon: '🏀', color: 'from-orange-500 to-red-500' },
  ], []);

  // Bet types for football
  const footballBetTypes = useMemo(() => [
    { id: 'match_winner', name: 'Match Winner', short: '1X2', icon: '🏆', marketType: 'match_winner' },
    { id: 'double_chance', name: 'Double Chance', short: 'DC', icon: '🔒', marketType: 'double_chance' },
    { id: 'over_under', name: 'Over/Under', short: 'O/U', icon: '📊', marketType: 'over_under' },
    { id: 'btts', name: 'Both Teams Score', short: 'BTTS', icon: '⚽⚽', marketType: 'btts' },
    { id: 'first_half', name: '1st Half', short: '1H', icon: '⏱️', marketType: 'first_half_winner' },
    { id: 'second_half', name: '2nd Half', short: '2H', icon: '⏱️', marketType: 'second_half_winner' }
  ], []);

  // Bet types for basketball
  const basketballBetTypes = useMemo(() => [
    { id: 'moneyline', name: 'Moneyline', short: 'ML', icon: '🏆', marketType: 'match_winner' },
    { id: 'point_spread', name: 'Point Spread', short: 'SP', icon: '📊', marketType: 'point_spread' },
    { id: 'total_points', name: 'Total Points', short: 'TP', icon: '🎯', marketType: 'total_points' }
  ], []);

  // Get bet types based on selected sport
  const betTypes = useMemo(() => {
    return selectedSport === 'basketball' ? basketballBetTypes : footballBetTypes;
  }, [selectedSport]);

  // League priority
  const getLeaguePriority = (leagueId, leagueName, sport = 'football') => {
    if (sport === 'football') {
      const priorityMap = { 1: 1, 2: 2, 39: 3, 140: 4, 135: 5, 78: 6, 61: 7 };
      if (priorityMap[leagueId]) return priorityMap[leagueId];
      const name = leagueName?.toLowerCase() || '';
      if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
      if (name.includes('champions league')) return 2;
      if (name.includes('premier league')) return 3;
      if (name.includes('la liga')) return 4;
      if (name.includes('serie a')) return 5;
      if (name.includes('bundesliga')) return 6;
      if (name.includes('ligue 1')) return 7;
      return 999;
    } else if (sport === 'basketball') {
      const priorityMap = { 13: 1, 12: 2, 178: 3, 14: 4 };
      if (priorityMap[leagueId]) return priorityMap[leagueId];
      const name = leagueName?.toLowerCase() || '';
      if (name.includes('wnba') || name.includes('nba w')) return 1;
      if (name.includes('nba')) return 2;
      if (name.includes('big3')) return 3;
      if (name.includes('nit')) return 4;
      return 999;
    }
    return 999;
  };

  // Group fixtures by league - handles both flat and grouped data
  const groupFixturesByLeague = useCallback((fixtures) => {
    if (!Array.isArray(fixtures) || fixtures.length === 0) return [];

    // Check if already grouped
    const firstItem = fixtures[0];
    if (firstItem.fixtures && Array.isArray(firstItem.fixtures)) {
      return fixtures;
    }

    // Flat array - group it
    const grouped = {};
    fixtures.forEach(fixture => {
      const leagueId = fixture.league_id;
      if (!grouped[leagueId]) {
        grouped[leagueId] = {
          league_id: leagueId,
          league_name: fixture.league_name || `League ${leagueId}`,
          league_logo: fixture.league_logo || null,
          country: fixture.country || null,
          fixtures: []
        };
      }
      grouped[leagueId].fixtures.push(fixture);
    });

    return Object.values(grouped);
  }, []);

  // Fetch football matches
  const fetchFootballMatches = useCallback(async () => {
    try {
      const response = await FootballAPI.getUpcoming(50);
      
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching football matches:', error);
      return [];
    }
  }, []);

  // Fetch basketball matches - FIXED: Handles flat array response
  const fetchBasketballMatches = useCallback(async () => {
    try {
      const response = await BasketballAPI.getUpcomingGames(50);
      
      console.log('Basketball API Response:', response);
      
      if (response.success && response.data) {
        // Check if data is already grouped or flat
        const grouped = groupFixturesByLeague(response.data);
        console.log('Grouped basketball data:', grouped);
        return grouped;
      }
      return [];
    } catch (error) {
      console.error('Error fetching basketball matches:', error);
      return [];
    }
  }, [groupFixturesByLeague]);

  // Fetch matches based on selected sport
  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      let filteredData = [];
      
      if (selectedSport === 'football') {
        filteredData = await fetchFootballMatches();
      } else if (selectedSport === 'basketball') {
        filteredData = await fetchBasketballMatches();
      } else {
        setGroupedMatches([]);
        setLoading(false);
        return;
      }
      
      console.log('Filtered data:', filteredData);
      
      if (!filteredData || filteredData.length === 0) {
        setGroupedMatches([]);
        setLoading(false);
        return;
      }
      
      // Sort leagues by priority
      filteredData.sort((a, b) => {
        const priorityA = getLeaguePriority(a.league_id, a.league_name, selectedSport);
        const priorityB = getLeaguePriority(b.league_id, b.league_name, selectedSport);
        return priorityA - priorityB;
      });

      // Limit to 10 fixtures total
      let allFixtures = [];
      filteredData.forEach(group => {
        if (group.fixtures && Array.isArray(group.fixtures)) {
          const fixtures = group.fixtures.map(fixture => ({
            ...fixture,
            league_id: group.league_id,
            league_name: group.league_name,
            league_logo: group.league_logo,
            country: group.country
          }));
          allFixtures = [...allFixtures, ...fixtures];
        }
      });

      const limitedFixtures = allFixtures.slice(0, 10);

      // Regroup the limited fixtures
      const regrouped = {};
      limitedFixtures.forEach(fixture => {
        const leagueId = fixture.league_id;
        if (!regrouped[leagueId]) {
          regrouped[leagueId] = {
            league_id: leagueId,
            league_name: fixture.league_name || `League ${leagueId}`,
            league_logo: fixture.league_logo || null,
            country: fixture.country || null,
            fixtures: []
          };
        }
        regrouped[leagueId].fixtures.push(fixture);
      });

      const finalGrouped = Object.values(regrouped).sort((a, b) => {
        const priorityA = getLeaguePriority(a.league_id, a.league_name, selectedSport);
        const priorityB = getLeaguePriority(b.league_id, b.league_name, selectedSport);
        return priorityA - priorityB;
      });

      console.log('Final grouped matches:', finalGrouped);
      setGroupedMatches(finalGrouped);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      setGroupedMatches([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSport, fetchFootballMatches, fetchBasketballMatches]);

  useEffect(() => {
    fetchMatches();
  }, [selectedSport, fetchMatches]);

  // Flatten matches for stats
  const allMatches = groupedMatches.flatMap(group => group.fixtures || []);
  
  const getLiveStatuses = () => {
    if (selectedSport === 'football') {
      return ['1H', 'HT', '2H', 'ET', 'P'];
    } else if (selectedSport === 'basketball') {
      return ['1Q', '2Q', 'HT', '3Q', '4Q', 'OT'];
    }
    return [];
  };
  
  const liveMatches = allMatches.filter(m => getLiveStatuses().includes(m.status));

  const handleSportSelect = useCallback((sportId) => {
    setSelectedSport(sportId);
    setSelectedBets([]);
    if (sportId === 'basketball') {
      setSelectedBetType('moneyline');
    } else {
      setSelectedBetType('match_winner');
    }
  }, []);

  const handleBetTypeSelect = useCallback((betTypeId) => {
    setSelectedBetType(betTypeId);
    setSelectedBets([]);
  }, []);

  const handleMatchCardClick = (matchId) => {
    navigate(`/match/${matchId}`);
  };

  const handleAddToBetslip = useCallback(() => {
    if (selectedBets.length === 0) return;
    
    selectedBets.forEach(bet => {
      addToBetSlip({
        matchId: bet.matchId,
        match: bet.matchName,
        selection: bet.selection,
        odds: bet.odds,
        marketType: bet.marketType,
        sport: bet.sport
      });
    });
    
    setSelectedBets([]);
    
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-24 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl z-50 animate-slide-up';
    toast.innerHTML = `✓ ${selectedBets.length} bet(s) added to betslip!`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }, [selectedBets, addToBetSlip]);

  const handleClearBets = useCallback(() => {
    setSelectedBets([]);
  }, []);

  // Update total odds
  useEffect(() => {
    if (selectedBets.length > 0) {
      const calculatedOdds = selectedBets.reduce((acc, bet) => acc * bet.odds, 1.0);
      setTotalOdds(parseFloat(calculatedOdds.toFixed(2)));
    } else {
      setTotalOdds(1.0);
    }
  }, [selectedBets]);

  const isTopLeague = (leagueId, leagueName) => {
    const priority = getLeaguePriority(leagueId, leagueName, selectedSport);
    return priority <= 7;
  };

  const isWorldCup = (leagueId, leagueName) => {
    return leagueId === 1 || leagueName?.toLowerCase().includes('world cup');
  };

  const isWNBA = (leagueId, leagueName) => {
    return leagueId === 13 || leagueName?.toLowerCase().includes('wnba') || leagueName?.toLowerCase().includes('nba w');
  };

  const handleViewAllSports = () => {
    navigate('/sports');
  };

  const getSportIcon = () => {
    if (selectedSport === 'football') return '⚽';
    if (selectedSport === 'basketball') return '🏀';
    return '🎯';
  };

  if (loading) {
    return (
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/20 rounded-2xl p-6 border border-gray-800 shadow-xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-4">Loading {selectedSport} matches...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/20 rounded-2xl p-6 border border-gray-800 shadow-xl overflow-hidden"
      aria-labelledby="quick-bet-heading"
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 animate-gradient-x" />
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl" />
      
      <div className="relative">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent">
              Quick Bet
            </h2>
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">
              LIVE ODDS
            </span>
          </div>
          <p className="text-gray-400 text-sm">Select a sport, choose a market, and place your bets</p>
        </div>

        {/* Sport Selector */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 hide-scrollbar">
          {sports.map((sport) => (
            <button
              key={sport.id}
              onClick={() => handleSportSelect(sport.id)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap transition-all duration-200 flex-shrink-0
                ${selectedSport === sport.id 
                  ? `bg-gradient-to-r ${sport.color} text-white shadow-lg scale-105` 
                  : 'bg-gray-800/70 text-gray-400 hover:bg-gray-700 hover:text-white'
                }
              `}
            >
              <span className="text-lg">{sport.icon}</span>
              <span className="font-medium text-sm">{sport.name}</span>
              {selectedSport === sport.id && (
                <span className="ml-1 w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
              )}
            </button>
          ))}
        </div>

    
        {/* View Controls */}
        {allMatches.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <button
              onClick={handleViewAllSports}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all hover:scale-105"
            >
              <FiEye className="w-4 h-4" />
              <span>View All</span>
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Matches Grid */}
        <div className="space-y-6">
          {groupedMatches.length === 0 && !loading ? (
            <div className="text-center py-12 bg-gray-800/50 rounded-xl">
              <div className="text-5xl mb-4">{getSportIcon()}</div>
              <h3 className="text-white font-bold text-lg mb-2">No {selectedSport} matches available</h3>
              <p className="text-gray-400 text-sm">Check back later for upcoming matches</p>
            </div>
          ) : (
            groupedMatches.map((group) => {
              const isWorldCupLeague = isWorldCup(group.league_id, group.league_name);
              const isTop = isTopLeague(group.league_id, group.league_name);
              const isWnbaLeague = isWNBA(group.league_id, group.league_name);
              
              return (
                <div key={group.league_id} className="space-y-3">
                  {/* League Header */}
                  <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg p-3">
                    {group.league_logo && (
                      <img src={group.league_logo} alt={group.league_name} className="w-5 h-5 object-contain" />
                    )}
                    <h3 className="font-bold text-white text-sm">{group.league_name}</h3>
                    {isWnbaLeague && (
                      <span className="text-[10px] bg-purple-700 text-white px-1.5 py-0.5 rounded">WNBA</span>
                    )}
                    {isWorldCupLeague && (
                      <span className="text-[10px] bg-yellow-700 text-white px-1.5 py-0.5 rounded">🏆</span>
                    )}
                    <span className="text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded-full">
                      {group.fixtures.length}
                    </span>
                  </div>
                  
                  {/* Matches */}
                  <div className={viewMode === 'grid' 
                    ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" 
                    : "space-y-3"
                  }>
                    {group.fixtures.map((match) => {
                      const matchWithOdds = {
                        ...match,
                        selectedBetType: selectedBetType,
                        onBetSelect: (matchId, betType, oddData) => {
                          const betKey = `${matchId}-${betType}-${oddData.label}`;
                          const betTypeObj = betTypes.find(bt => bt.id === betType);
                          
                          setSelectedBets(prev => {
                            const isSelected = prev.some(bet => bet.key === betKey);
                            if (isSelected) {
                              return prev.filter(bet => bet.key !== betKey);
                            } else {
                              return [...prev, {
                                key: betKey,
                                matchId: matchId,
                                matchName: `${match.home_team} vs ${match.away_team}`,
                                selection: oddData.outcome,
                                odds: parseFloat(oddData.value),
                                marketType: betTypeObj?.marketType || betType,
                                sport: selectedSport
                              }];
                            }
                          });
                        }
                      };
                      
                      return (
                        <div key={match.id} onClick={() => handleMatchCardClick(match.id)}>
                          <MatchCard
                            match={matchWithOdds}
                            sport={selectedSport}
                            oddsFormat={oddsFormat}
                            viewMode={viewMode}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
          
          {/* View All Button */}
          {allMatches.length > 0 && (
            <div className="flex justify-center pt-4 mt-4 border-t border-gray-800">
              <button
                onClick={handleViewAllSports}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all hover:scale-105 group"
              >
                <FiEye className="w-5 h-5" />
                <span>View All Matches</span>
                <FiChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}
        </div>

        {/* Selected Bets Summary */}
        {selectedBets.length > 0 && (
          <div className="sticky bottom-4 mt-8 p-5 bg-gradient-to-r from-gray-800 to-gray-900 border border-blue-500/30 rounded-xl shadow-xl animate-slide-up z-50">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                  <h3 className="text-white font-bold text-lg">
                    {selectedBets.length} Selection{selectedBets.length > 1 ? 's' : ''}
                  </h3>
                </div>
                <div className="text-gray-400 text-sm">Ready to add to betslip</div>
                
                <div className="mt-4 flex items-center gap-4">
                  <div className="bg-blue-500/20 px-4 py-2 rounded-lg">
                    <div className="text-xs text-blue-400">Total Odds</div>
                    <div className="text-white font-bold text-xl">{totalOdds}</div>
                  </div>
                  <div className="text-sm text-gray-400">
                    Potential Win: <span className="text-green-400 font-bold">${(10 * totalOdds).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={handleClearBets}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors"
                >
                  🗑️ Clear
                </button>
                <button 
                  onClick={handleAddToBetslip}
                  className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-green-500/30"
                >
                  🎯 Add to Betslip
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QuickSelection;